import {
  useEditCaSubmittalMutation,
  useGetCasMetadataQuery,
  useGetCaSubmittalsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { CASStatus, CASStatusItem, Permission } from '@cims-online/shared'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useCallback } from 'react'
import { WithPayItemGlobalId } from 'components/CIMSTable/components/HeaderActionButtons/types'
import { SubmittalTrackerContext, Submittal } from './types'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } = createStrictContext<SubmittalTrackerContext>(
  'SubmittalTrackerContext'
)

export const useSubmittalTrackerContext = useContextFn

const SubmittalTrackerProvider: React.FC<WithPayItemGlobalId> = ({
  payItemGlobalId,
  children,
}) => {
  const { projectId } = useProjectContext()
  const {
    refetch,
    loading: fetching,
    data: submittalData,
  } = useGetCaSubmittalsQuery({
    ...DefaultQueryHookOptions,
    variables: {
      projectId,
      input: { projectId: { equals: projectId } },
    },
  })
  const {
    data: metadata,
    loading: metaFetching,
    refetch: refetchMetadata,
  } = useGetCasMetadataQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId! },
  })
  const actionModal = useCIMSModalValues<Submittal>()
  const createModal = useCIMSModalValues<Submittal>()
  const revertModal = useCIMSModalValues<Submittal>()
  const pdfModal = useCIMSModalValues<Submittal>()
  const canReviewAll = useCheckPermission(
    Permission.ReviewAllConstructionAdminSubmittal
  )
  const canRevise: boolean = useCheckPermission(
    Permission.ReviseConstructionAdminSubmittal
  )
  const canRoute: boolean = useCheckPermission(
    Permission.RouteConstructionAdminSubmittal
  )
  const canGroupReview: boolean = useCheckPermission(
    Permission.GroupReviewConstructionAdminSubmittal
  )
  const canDisposition: boolean = useCheckPermission(
    Permission.GiveDispositionConstructionAdminSubmittal
  )
  const canAdvance = useCallback(
    (
      status: CASStatusItem,
      casToCASRoutingGroups: Submittal['casToCASRoutingGroups']
    ): boolean => {
      if (status === CASStatus.RevisionsRequested) return canRevise
      else if (status === CASStatus.RouterReview) return canRoute
      else if (status === CASStatus.REReview) return canDisposition
      else if (status === CASStatus.GroupReview)
        return (
          canGroupReview &&
          !!casToCASRoutingGroups.length &&
          casToCASRoutingGroups.every(({ accepted }) => !!accepted)
        )
      else return false
    },
    [canDisposition, canGroupReview, canRevise, canRoute]
  )
  const dispositionRequiresComment = useCallback<
    SubmittalTrackerContext['dispositionRequiresComment']
  >(
    (id) => {
      if (!id) return true
      return (
        metadata?.project?.casDispositionTypes?.find((type) => type.id === id)
          ?.requiresComments ?? false
      )
    },
    [metadata?.project?.casDispositionTypes]
  )
  const [edit] = useEditCaSubmittalMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editConstructionAdminSubmittal: {
        submittal: { sharePointFolderName },
      },
    }) => simpleEditNotification(sharePointFolderName),
  })
  return (
    <Provider
      key="submittal-tracker-data-provider"
      value={{
        payItemGlobalId,
        records: submittalData?.constructionAdminSubmittals?.records ?? [],
        allPackageNumbers:
          submittalData?.constructionAdminSubmittals?.records?.map(
            ({ packageNumber }) => packageNumber.trim()
          ) ?? [],
        refetch,
        fetching,
        selectOptions: {
          typeOptions: useOptions({
            items: metadata?.project?.casTypes,
            value: 'id',
            label: 'name',
            tooltip: 'description',
            sort: 'name',
          }),
          subtypeOptions: useOptions({
            items: metadata?.project?.casSubtypes,
            value: 'id',
            label: 'name',
            tooltip: 'description',
            sort: 'name',
          }),
          routingGroupOptions: useOptions({
            items: metadata?.project?.casRoutingGroups,
            value: 'id',
            label: 'name',
            tooltip: 'description',
            sort: 'name',
          }),
          dispositionOptions: useOptions({
            items: metadata?.project?.casDispositionTypes,
            value: 'id',
            label: 'name',
            tooltip: 'description',
            sort: 'name',
          }),
        },
        actionModal,
        createModal,
        revertModal,
        pdfModal,
        permissions: {
          canReviewAll,
          canRevise,
          canRoute,
          canGroupReview,
          canDisposition,
        },
        canAdvance,
        quickLinks: metadata?.project?.casQuickLinks ?? [],
        metaFetching,
        refetchMetadata,
        dispositionRequiresComment,
        edit: async (input) => {
          await edit({ variables: { input } })
        },
      }}
    >
      {children}
    </Provider>
  )
}

export default SubmittalTrackerProvider
